import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Card, Modal, Pagination, Space, Table, Tag, Tooltip, notification } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import CustomButton from 'components/custom-button';
import { useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import { formatedDate } from 'utils/common';
import { shortenName } from 'utils/short-name';
import PageTitle from '../../components/page-title';
import ModalCreateEdit from './component/ModalCreateEdit';
import { BannerSizeTip } from './component/BannerSizeGuide';
import styles from './styles.module.scss';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { TYPE_BANNER } from 'constants/common';

const LIMIT = 10;

const initFilter = { page: 1, limit: LIMIT };

// Giá trị lưu ở server là chuỗi "HOME,PK"...; chỉ đổi chữ hiển thị.
const BANNER_PLACE_LABEL: Record<string, string> = {
  [TYPE_BANNER.HOME]: 'Trang chủ',
  [TYPE_BANNER.LIVE_SHOW]: 'Liveshow',
  [TYPE_BANNER.PK]: 'PK',
};

export interface Filter {
  limit: number;
  page: number;
}

const BannerManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(initFilter as Filter);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [statusUpdateData, setStatusUpdateData] = useState(false);
  const [isOpenModalEditAndCreate, setIsOpenModalEditAndCreate] = useState(false);
  const [valueUserSelected, setvalueUserSelected] = useState<any>(null);

  const onFinish = (value: Partial<Filter>) => {
    setFilter((preState) => ({ ...preState, ...value, page: 1 }));
  };

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  const hasSelected = selectedRowKeys.length > 0;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.banners, {
          ...filter,
        });
        setDataResponse({ items: res?.data?.data, totalCount: res?.data?.metadata?.total });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [filter, statusUpdateData]);

  const columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      ellipsis: true,
      key: 'id',
    },
    {
      title: 'Tên',
      dataIndex: 'name',
      key: 'name',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 70) || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Hiển thị ở',
      dataIndex: 'type',
      key: 'type',
      render: (value) => (
        <Space size={[4, 4]} wrap>
          {String(value || '')
            .split(',')
            .filter(Boolean)
            .map((place) => (
              <Tag key={place} style={{ marginInlineEnd: 0 }}>
                {BANNER_PLACE_LABEL[place.trim()] || place}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: 'Banner',
      dataIndex: 'image',
      key: 'image',
      // Ảnh 136px + 2 × 16px đệm ô; không đặt độ rộng thì ảnh tràn sang cột bên cạnh ở màn hẹp.
      width: 168,
      render: (value) => (
        <img
          width={136}
          height={72}
          className={styles.thumb}
          src={`${SERVER_FILE_ENDPOINT}/${value}`}
          alt=""
        />
      ),
    },
    {
      title: 'Liên kết',
      dataIndex: 'link',
      key: 'link',
      render: (value) => (
        <Tooltip title={value}>
          <span>{shortenName(value || '', 40) || ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (value) => <span className={styles.nowrap}>{formatedDate(value)}</span>,
    },
    {
      title: 'Thao tác',
      width: 104,
      key: 'action',
      render: (_, record) => (
        <Tooltip title="Sửa">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handelOpenCreateEditModal(record)}
          />
        </Tooltip>
      ),
    },
  ];
  const onChangeStatusUpdateData = () => setStatusUpdateData(!statusUpdateData);

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  const handelOpenDeleteModal = () => {
    setIsModalDeleteOpen(true);
  };

  const handleOkDeleteModal = async () => {
    setLoading(true);
    try {
      await AxiosUtils._delete(API.deleteBanners, { ids: selectedRowKeys });
      onChangeStatusUpdateData();
      setSelectedRowKeys([]);
      notification.success({
        message: 'Thành công',
        description: 'Xóa thành công!',
        duration: 4,
      });
    } catch (error) {
      notification.error({
        message: 'Không thành công',
        description: 'Có lỗi xảy ra, vui lòng thử lại.',
        duration: 4,
      });
    } finally {
      setLoading(false);
      setIsModalDeleteOpen(false);
    }
  };

  const handleCancelDeleteModal = () => {
    setIsModalDeleteOpen(false);
  };

  const handelOpenCreateEditModal = (value: any) => {
    setIsOpenModalEditAndCreate(true);
    setvalueUserSelected(value);
  };

  const handleCancelCreateEditModal = () => {
    setIsOpenModalEditAndCreate(false);
    setvalueUserSelected(null);
  };

  return (
    <div>
      <PageTitle
        title="Banner"
        extra={
          <Space wrap>
            {hasSelected && (
              <Button danger icon={<DeleteOutlined />} onClick={handelOpenDeleteModal}>
                Xóa {selectedRowKeys.length} banner
              </Button>
            )}
            <CustomButton
              onClick={() => handelOpenCreateEditModal(valueUserSelected)}
              bgColor="primary-green"
              text="Thêm banner"
              icon={<PlusOutlined />}
            />
          </Space>
        }
      />
      <BannerSizeTip />
      <Card
        className="custom-table"
        styles={{ body: { padding: 0 } }}
        style={{ overflow: 'hidden' }}
      >
        <Table
          rowSelection={rowSelection}
          columns={columns}
          rowKey="id"
          pagination={false}
          dataSource={dataResponse.items}
          scroll={{ x: 900 }}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <div className={styles.pagination}>
            <Pagination
              showSizeChanger={false}
              current={filter.page}
              onChange={handleChangePage}
              showLessItems
              total={dataResponse.totalCount}
              pageSize={LIMIT}
            />
          </div>
        )}
      </Card>

      {isOpenModalEditAndCreate && (
        <ModalCreateEdit
          isOpen={isOpenModalEditAndCreate}
          handleCancelCreateEditModal={handleCancelCreateEditModal}
          valueUserSelected={valueUserSelected}
          onChangeStatusUpdateData={onChangeStatusUpdateData}
        />
      )}
      <Modal
        title="Xóa banner"
        open={isModalDeleteOpen}
        onOk={handleOkDeleteModal}
        onCancel={handleCancelDeleteModal}
        okText="Xóa"
        cancelText="Hủy"
        okButtonProps={{ danger: true }}
        confirmLoading={loading}
      >
        <p>Bạn có chắc muốn xóa {selectedRowKeys.length} banner đã chọn?</p>
      </Modal>
    </div>
  );
};

export default BannerManager;
